import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Loader2, Plus, ListChecks, Trash2, History, Check, Users, ArrowRightLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, PageState, Empty, Field, UpgradeGate } from "@/components/app/Common";
import { SimpleSelect, entityOptions } from "@/components/app/SimpleSelect";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg } from "@/lib/api";
import { fmtDate, daysLabel } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";

const TONE = { calm: "border-emerald-500/50 bg-emerald-500/[0.06]", watch: "border-amber-500/50 bg-amber-500/[0.06]", action: "border-red-500/50 bg-red-500/[0.06]" };
const KINDS = [["task", "Task"], ["action_item", "Action item"], ["follow_up", "Follow-up"], ["commitment", "Commitment / promise"], ["delegated", "Delegated task"], ["dependency", "External dependency"]];
export const PARTIES = [["customer", "Customer"], ["vendor", "Vendor"], ["accountant", "Accountant"], ["lawyer", "Lawyer"], ["employee", "Employee"], ["government", "Government / registry"], ["other", "Other third party"]];
const STATUSES = [["open", "Open"], ["in_progress", "In progress"], ["waiting", "Waiting on others"], ["done", "Done"], ["cancelled", "Cancelled"]];
const RELATED = [["other", "General"], ["filing", "Filing"], ["client", "Client"], ["vendor", "Vendor"], ["employee", "Employee"], ["project", "Project"], ["acquisition", "Acquisition"]];
const opts = (l) => l.map(([value, label]) => ({ value, label }));
const days = (d) => (d ? Math.round((new Date(d) - new Date(new Date().toDateString())) / 86400000) : null);

const SAMPLE = { status: "watch", headline: "Two things need you this week: an overdue Ontario annual return and a supplier follow-up.",
  team: [{ owner: "Sarah", done: 14, prev: 11, delta: 3, open: 6, overdue: 0, waiting: 1 }, { owner: "Tristan", done: 18, prev: 13, delta: 5, open: 4, overdue: 0, waiting: 0 }, { owner: "Mike", done: 9, prev: 11, delta: -2, open: 15, overdue: 4, waiting: 3 }],
  team_notes: ["Mike has 15 open items and 4 overdue while completions dipped — consider moving two follow-ups to Tristan."],
  rebalance: [{ id: "sample", from: "Mike", to: "Tristan", state: "pending", reason: "Mike has 5 follow-ups due this week; Tristan has capacity.", tasks: [{ id: "a", title: "Call ABC Manufacturing about renewal" }, { id: "b", title: "Send Q3 statement to Northwind" }] }],
  wins: ["12 receipts processed automatically", "3 commitments completed", "1 filing submitted", "1 acquisition opportunity advanced to due diligence"],
  trend: { direction: "improving", weeks: [["Week 1", 4, 3, 1, 4], ["Week 2", 5, 3, 2, 5], ["Week 3", 6, 4, 2, 6], ["Week 4", 7, 5, 3, 7]].map(([label, commitments, follow_ups, filings, other]) => ({ label, commitments, follow_ups, filings, other, total: commitments + follow_ups + filings + other })) },
  priorities: [{ title: "File Brightline's Ontario Annual Return", why: "12 days late; dissolution risk grows each month" }, { title: "Decide on the Cedar Peak LOI", why: "Seller asked for an answer by Friday" }],
  follow_ups: [{ title: "Payroll provider quote", with: "Maria (ops)", note: "waiting 9 days" }], recommended_actions: ["Block 30 minutes Monday to clear the filing", "Review 2 flagged receipts"] };

const ISO = /\d{4}-\d{2}-\d{2}/;

const AddTask = ({ item, kind, onAdd, tracked }) => {
  const [state, setState] = useState(tracked ? "done" : "idle");
  const add = async () => {
    setState("busy");
    try {
      await onAdd(item, kind);
      setState("done");
    } catch (e) {
      toast.error(errMsg(e));
      setState("idle");
    }
  };
  if (state === "done") return <span className="ml-2 inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 shrink-0" data-testid="briefing-task-added"><Check className="h-3.5 w-3.5" />Tracked</span>;
  return (
    <button type="button" onClick={add} disabled={state === "busy"} data-testid="briefing-add-task-btn"
      className="ml-2 inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-xs text-muted-foreground shrink-0 transition-colors hover:border-primary hover:text-primary disabled:opacity-60">
      {state === "busy" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}Task
    </button>
  );
};

const List = ({ title, items, render, kind, onAdd, tracked }) => (items?.length ? (
  <div className="mt-4"><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{title}</div>
    <ul className="space-y-1.5 text-sm">{items.map((x, i) => (
      <li key={i} className="flex gap-2 items-start"><span className="mt-2 h-1 w-1 rounded-full bg-primary shrink-0" /><span className="flex-1">{render(x)}</span>
        {onAdd && kind && <AddTask item={x} kind={kind} onAdd={onAdd} tracked={tracked?.has(toTask(x, kind).title.trim().toLowerCase())} />}</li>))}</ul></div>) : null);

const SERIES = [["commitments", "bg-emerald-500", "Commitments"], ["follow_ups", "bg-blue-500", "Follow-ups"], ["filings", "bg-amber-500", "Filings & taxes"], ["other", "bg-slate-400", "Other work"]];
const DIR = { improving: ["▲ Improving", "text-emerald-600 dark:text-emerald-400"], slowing: ["▼ Slowing", "text-red-500"], steady: ["● Holding steady", "text-muted-foreground"] };

const Trend = ({ t, testId }) => {
  if (!t?.weeks?.some((w) => w.total)) return null;
  const top = Math.max(...t.weeks.map((w) => w.total), 1);
  return (
    <div className="mt-4 rounded-xl border border-border p-4" data-testid={`${testId}-trend`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Execution velocity · items closed per week</div>
        <span className={`text-xs font-semibold ${DIR[t.direction][1]}`} data-testid={`${testId}-trend-direction`}>{DIR[t.direction][0]}</span>
      </div>
      <div className="space-y-2">
        {t.weeks.map((w, i) => (
          <div key={w.label} className="flex items-center gap-3 text-xs" data-testid={`${testId}-trend-week-${i}`}>
            <span className="w-24 shrink-0 text-muted-foreground">{w.label}</span>
            <div className="flex-1 h-3.5 rounded-full bg-muted overflow-hidden flex">
              {SERIES.map(([k, c, l]) => w[k] > 0 && <div key={k} className={`${c} h-full transition-[width] duration-700`} style={{ width: `${(w[k] / top) * 100}%` }} title={`${l}: ${w[k]}`} />)}
            </div>
            <span className="w-8 text-right font-mono font-semibold">{w.total}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">{SERIES.map(([k, c, l]) => <span key={k} className="flex items-center gap-1.5"><span className={`h-2 w-2 rounded-sm ${c}`} />{l}</span>)}</div>
    </div>
  );
};

const Suggestion = ({ s, onDone, testId }) => {
  const [busy, setBusy] = useState("");
  const [state, setState] = useState(s.state);
  const act = async (action) => {
    setBusy(action);
    try {
      const r = await api.post(`/cos/rebalance/${s.id}?action=${action}`);
      setState(r.data.state);
      if (action === "accept") toast.success(`Moved ${r.data.moved} task(s) to ${s.to}`);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy("");
  };
  return (
    <div className="rounded-lg border border-border p-3" data-testid={`${testId}-rebalance-${s.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold flex items-center gap-1.5"><ArrowRightLeft className="h-4 w-4 text-primary" />Move {s.tasks.length} task{s.tasks.length > 1 ? "s" : ""} from {s.from} to {s.to}</span>
        {state === "pending" && onDone ? (
          <span className="flex gap-2">
            <Button size="sm" onClick={() => act("accept")} disabled={!!busy} data-testid={`rebalance-accept-${s.id}`}>{busy === "accept" && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}Accept</Button>
            <Button size="sm" variant="ghost" onClick={() => act("dismiss")} disabled={!!busy} data-testid={`rebalance-dismiss-${s.id}`}>Dismiss</Button>
          </span>
        ) : state !== "pending" && <span className={`text-xs ${state === "accepted" ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`} data-testid={`rebalance-state-${s.id}`}>{state === "accepted" ? "Reassigned" : "Dismissed"}</span>}
      </div>
      <p className="text-sm text-muted-foreground mt-1">{s.reason}</p>
      <ul className="mt-1.5 text-xs text-muted-foreground list-disc pl-5">{s.tasks.map((t) => <li key={t.id}>{t.title}</li>)}</ul>
    </div>
  );
};

const Team = ({ team, notes, rebalance, onRebalanced, testId }) => (team?.length ? (
  <div className="mt-4 rounded-xl border border-border p-4" data-testid={`${testId}-team`}>
    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2"><Users className="h-3.5 w-3.5" />Team velocity · this week</div>
    <div className="divide-y divide-border">
      {team.slice(0, 10).map((r, i) => (
        <div key={r.owner} className="flex flex-wrap items-center gap-x-4 gap-y-0.5 py-2 text-sm" data-testid={`${testId}-team-row-${i}`}>
          <span className="flex-1 min-w-[120px] font-medium">{r.owner}</span>
          <span><b className="font-mono">{r.done}</b> completed <span className={`text-xs ${r.delta > 0 ? "text-emerald-600 dark:text-emerald-400" : r.delta < 0 ? "text-red-500" : "text-muted-foreground"}`}>({r.delta > 0 ? "+" : ""}{r.delta} vs prior week)</span></span>
          <span className="text-xs text-muted-foreground sm:w-36 sm:text-right">{r.open} open{r.overdue > 0 && <span className="text-red-500"> · {r.overdue} overdue</span>}{r.waiting > 0 && ` · ${r.waiting} waiting`}</span>
        </div>
      ))}
    </div>
    {notes?.length > 0 && <ul className="mt-3 space-y-1 text-sm" data-testid={`${testId}-team-notes`}>{notes.map((n) => <li key={n} className="flex gap-2"><Sparkles className="h-3.5 w-3.5 mt-1 text-primary shrink-0" />{n}</li>)}</ul>}
    {rebalance?.length > 0 && <div className="mt-3 space-y-2" data-testid={`${testId}-rebalance`}><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Suggested rebalancing</div>{rebalance.map((s) => <Suggestion key={s.id} s={s} onDone={onRebalanced} testId={testId} />)}</div>}
  </div>
) : null);

export const BriefingView = ({ b, testId = "briefing", onAdd, tracked, onRebalanced }) => (
  <div data-testid={testId}>
    <p className="text-base font-medium" data-testid={`${testId}-headline`}>{b.headline}</p>
    {b.wins?.length > 0 && (
      <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.07] p-4" data-testid={`${testId}-wins`}>
        <div className="text-xs font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mb-2">Closed this week</div>
        <ul className="grid gap-1.5 sm:grid-cols-2 text-sm">{b.wins.map((w) => <li key={w} className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-500 shrink-0" />{w}</li>)}</ul>
      </div>
    )}
    <Trend t={b.trend} testId={testId} />
    <Team team={b.team} notes={b.team_notes} rebalance={b.rebalance} onRebalanced={onRebalanced} testId={testId} />
    <List title="Priorities" kind="action_item" onAdd={onAdd} tracked={tracked} items={b.priorities} render={(x) => <span><b>{x.title}</b> <span className="text-muted-foreground">— {x.why}</span></span>} />
    <List title="Open commitments" kind="commitment" onAdd={onAdd} tracked={tracked} items={b.commitments} render={(x) => <span>{x.title} <span className="text-muted-foreground">({x.owner || "unassigned"}, {x.due || "no date"})</span></span>} />
    <List title="Follow-ups" kind="follow_up" onAdd={onAdd} tracked={tracked} items={b.follow_ups} render={(x) => <span>{x.title} <span className="text-muted-foreground">— {x.with} {x.note}</span></span>} />
    <List title="Upcoming deadlines" kind="task" onAdd={onAdd} tracked={tracked} items={b.deadlines} render={(x) => <span>{x.title} <span className="text-muted-foreground">— {x.due}</span></span>} />
    <List title="Financial activity" items={b.financial} render={(x) => x} />
    <List title="Stalled — waiting on others" kind="follow_up" onAdd={onAdd} tracked={tracked} items={b.stalled} render={(x) => <span><b>{x.title}</b> <span className="text-muted-foreground">({x.with}) — {x.action}</span></span>} />
    <List title="Decisions to revisit" items={b.decisions} render={(x) => <span><b>{x.title}</b>: <span className="text-muted-foreground">{x.note}</span></span>} />
    <List title="Deals" kind="follow_up" onAdd={onAdd} tracked={tracked} items={b.deals} render={(x) => <span><b>{x.name}</b>: {x.note}</span>} />
    <List title="Across your companies" items={b.cross_entity} render={(x) => x} />
    <List title="Recommended this week" kind="action_item" onAdd={onAdd} tracked={tracked} items={b.recommended_actions} render={(x) => x} />
  </div>
);

const toTask = (x, kind) => {
  if (typeof x === "string") return { title: x.slice(0, 200), kind, notes: "From your Chief of Staff briefing" };
  const title = (x.name ? `${x.name}: ${x.note || "follow up"}` : x.title || "").slice(0, 200);
  const due = [x.due].find((d) => d && ISO.test(d))?.match(ISO)[0] || null;
  const notes = [x.why, x.with && `With: ${x.with}`, x.note && !x.name && x.note, "From your Chief of Staff briefing"].filter(Boolean).join("\n");
  return { title, kind, due_date: due, owner: (x.owner && x.owner !== "unassigned" ? x.owner : x.with || "").slice(0, 80), notes: notes.slice(0, 4000),
    related_type: x.name ? "acquisition" : "other", related_name: (x.name || "").slice(0, 120) };
};

const Briefing = ({ tasks, onAdded }) => {
  const state = useLoad(() => api.get("/cos/briefing").then((r) => r.data));
  const [busy, setBusy] = useState(false);
  const refresh = async () => {
    setBusy(true);
    try {
      await api.post("/cos/briefing");
      state.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const rec = state.data;
  const tracked = new Set((tasks || []).map((t) => t.title.trim().toLowerCase()));
  const add = async (x, kind) => {
    const t = toTask(x, kind);
    if (!t.title.trim()) return;
    await api.post("/tasks", t);
    toast.success("Added to your tasks");
    onAdded();
  };
  return (
    <div className={`surface p-6 border-l-4 ${TONE[rec?.briefing?.status] || ""}`} data-testid="briefing-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 font-semibold"><Sparkles className="h-4 w-4 text-primary" />This week's briefing
          {rec && <span className="text-xs font-normal text-muted-foreground">· {fmtDate(rec.created_at)}</span>}</div>
        <Button size="sm" variant="outline" onClick={refresh} disabled={busy} data-testid="briefing-refresh-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}{rec ? "Refresh" : "Generate briefing"}</Button>
      </div>
      <PageState state={state}>
        {rec ? <BriefingView b={rec.briefing} onAdd={add} tracked={tracked} onRebalanced={onAdded} /> : <p className="text-sm text-muted-foreground" data-testid="briefing-empty">No briefing yet. Generate one now — it also arrives every Monday with your health score.</p>}
      </PageState>
    </div>
  );
};

const blank = { title: "", kind: "task", status: "open", priority: "normal", due_date: "", owner: "", entity_id: "", related_type: "other", related_name: "", notes: "", waiting_on_type: "", waiting_on_name: "", decision_id: "" };

export const TaskForm = ({ ents, onSaved, dealId, waiting }) => {
  const [f, setF] = useState(waiting ? { ...blank, kind: "dependency", waiting_on_type: "customer" } : blank);
  const people = useLoad(() => api.get("/tasks/assignees").then((r) => r.data));
  const decisions = useLoad(() => api.get("/decisions").then((r) => r.data.decisions).catch(() => []));
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tasks", { ...f, status: f.waiting_on_type ? "waiting" : f.status, due_date: f.due_date || null, entity_id: f.entity_id || null,
        decision_id: f.decision_id || null, deal_id: dealId || null, related_type: dealId ? "acquisition" : f.related_type });
      setF(waiting ? { ...blank, kind: "dependency", waiting_on_type: "customer" } : blank);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={save} className="grid gap-3 sm:grid-cols-6 items-end" data-testid="task-form">
      <div className="sm:col-span-3"><Field label="What needs doing"><Input value={f.title} onChange={set("title")} required placeholder="e.g. Send signed engagement letter to accountant" data-testid="task-title-input" /></Field></div>
      <Field label="Type"><SimpleSelect value={f.kind} onChange={set("kind")} options={opts(KINDS)} testId="task-kind-select" /></Field>
      <Field label="Due"><Input type="date" value={f.due_date} onChange={set("due_date")} data-testid="task-due-input" /></Field>
      <Field label="Owner (team email gets reminders)"><Input value={f.owner} onChange={set("owner")} placeholder="Name or email" list={`assignees-${dealId || "main"}`} data-testid="task-owner-input" />
        <datalist id={`assignees-${dealId || "main"}`}>{(people.data || []).map((p) => <option key={p.email} value={p.email}>{p.label}</option>)}</datalist></Field>
      {!dealId && <div className="sm:col-span-2"><Field label="Entity"><SimpleSelect value={f.entity_id} onChange={set("entity_id")} options={entityOptions(ents)} allowNone noneLabel="No entity" scoped testId="task-entity-select" /></Field></div>}
      {!dealId && <Field label="Linked to"><SimpleSelect value={f.related_type} onChange={set("related_type")} options={opts(RELATED)} testId="task-related-select" /></Field>}
      {!dealId && <div className="sm:col-span-2"><Field label="Name (client, vendor, person…)"><Input value={f.related_name} onChange={set("related_name")} data-testid="task-related-name-input" /></Field></div>}
      <Field label="Waiting on"><SimpleSelect value={f.waiting_on_type} onChange={set("waiting_on_type")} options={opts(PARTIES)} allowNone={!waiting} noneLabel="Not waiting" testId="task-waiting-type-select" /></Field>
      {f.waiting_on_type && <div className="sm:col-span-2"><Field label="Who exactly"><Input value={f.waiting_on_name} onChange={set("waiting_on_name")} required={waiting} placeholder="e.g. Dana at Miller CPA" data-testid="task-waiting-name-input" /></Field></div>}
      {decisions.data?.length > 0 && <div className="sm:col-span-2"><Field label="Follows from decision"><SimpleSelect value={f.decision_id} onChange={set("decision_id")} options={decisions.data.map((d) => ({ value: d.id, label: d.title }))} allowNone noneLabel="No decision" testId="task-decision-select" /></Field></div>}
      <Button type="submit" data-testid="task-add-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
    </form>
  );
};

const TaskRow = ({ t, reload, canEdit }) => {
  const [hist, setHist] = useState(false);
  const d = days(t.due_date);
  const update = async (status) => {
    try {
      await api.put(`/tasks/${t.id}`, { ...t, status });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="py-3" data-testid={`task-row-${t.id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
        <span className={`flex-1 min-w-[220px] font-medium ${t.status === "done" ? "line-through text-muted-foreground" : ""}`}>{t.title}
          <span className="block text-xs font-normal text-muted-foreground">{KINDS.find((k) => k[0] === t.kind)?.[1]}{t.related_name && ` · ${t.related_name}`}{t.entity_name && ` · ${t.entity_name}`}{t.owner && ` · ${t.owner}`}
            {t.status === "waiting" && t.waiting_on_type && <span className="text-amber-600 dark:text-amber-400" data-testid={`task-waiting-on-${t.id}`}> · waiting on {t.waiting_on_name || PARTIES.find((p) => p[0] === t.waiting_on_type)?.[1]}</span>}</span></span>
        {t.due_date && <span className={`text-xs font-mono ${d < 0 && t.status !== "done" ? "text-red-500" : "text-muted-foreground"}`}>{fmtDate(t.due_date)} {t.status !== "done" && `· ${daysLabel(d)}`}</span>}
        {t.nudges && Object.keys(t.nudges).length > 0 && t.status !== "done" && <span className="text-[11px] rounded-full bg-amber-500/12 text-amber-600 dark:text-amber-400 px-2 py-0.5" data-testid={`task-nudged-${t.id}`}>{Object.keys(t.nudges).some((k) => k.startsWith("overdue_3") || k.startsWith("overdue_7")) ? "Escalated" : "Reminded"}</span>}
        <div className="w-44">{canEdit ? <SimpleSelect value={t.status} onChange={update} options={opts(STATUSES)} testId={`task-status-${t.id}`} /> : <span className="text-xs">{t.status}</span>}</div>
        <Button size="icon" variant="ghost" onClick={() => setHist(!hist)} aria-label="History" data-testid={`task-history-btn-${t.id}`}><History className="h-3.5 w-3.5" /></Button>
        {canEdit && <Button size="icon" variant="ghost" onClick={async () => { await api.delete(`/tasks/${t.id}`); reload(); }} aria-label="Delete" data-testid={`task-delete-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
      </div>
      {hist && <ul className="mt-2 ml-1 border-l border-border pl-3 text-xs text-muted-foreground space-y-0.5" data-testid={`task-history-${t.id}`}>{(t.history || []).map((h, i) => <li key={i}>{fmtDate(h.at)} — {h.event}</li>)}</ul>}
    </div>
  );
};

const GROUPS = [["Overdue", (t) => !["done", "cancelled", "waiting"].includes(t.status) && days(t.due_date) < 0], ["Waiting on others", (t) => t.status === "waiting"],
  ["This week", (t) => !["done", "cancelled", "waiting"].includes(t.status) && days(t.due_date) >= 0 && days(t.due_date) <= 7],
  ["Later / no date", (t) => !["done", "cancelled", "waiting"].includes(t.status) && (t.due_date == null || days(t.due_date) > 7)],
  ["Done recently", (t) => t.status === "done"]];

export const TaskList = ({ tasks, reload, canEdit }) => {
  const used = new Set();
  return GROUPS.map(([label, fn]) => {
    const items = tasks.filter((t) => !used.has(t.id) && fn(t)).slice(0, label.startsWith("Done") ? 10 : 100);
    items.forEach((t) => used.add(t.id));
    return items.length ? (
      <div key={label} className="mt-5" data-testid={`task-group-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label} · {items.length}</div>
        <div className="divide-y divide-border">{items.map((t) => <TaskRow key={t.id} t={t} reload={reload} canEdit={canEdit} />)}</div>
      </div>) : null;
  });
};

export default function ChiefOfStaff() {
  const { user } = useAuth();
  const state = useLoad(() => Promise.all([api.get("/tasks"), api.get("/entities")]).then(([a, b]) => ({ tasks: a.data, ents: b.data })));
  const owner = user?.workspace?.role === "owner";
  const canEdit = user?.workspace?.role !== "viewer";
  if (state.gated) {
    return (
      <div>
        <PageHeader eyebrow="AI Chief of Staff" title="Your digital chief of staff" subtitle="Priorities, commitments and follow-ups surfaced for you every week." />
        <div className="surface p-6 mb-6 opacity-90 border-l-4 border-amber-500/50" data-testid="briefing-sample"><div className="text-xs font-semibold text-muted-foreground mb-2">EXAMPLE BRIEFING</div><BriefingView b={SAMPLE} testId="briefing-sample-view" /></div>
        <UpgradeGate gated={state.gated} />
      </div>
    );
  }
  return (
    <div>
      <PageHeader eyebrow="AI Chief of Staff" title="What deserves your attention" subtitle="Claude reads your tasks, deadlines, bookkeeping and deals, and tells you what matters this week." />
      {owner && <Briefing tasks={state.data?.tasks} onAdded={state.reload} />}
      <PageState state={state}>
        <div className="surface p-6 mt-8" data-testid="tasks-panel">
          <div className="flex items-center gap-2 font-semibold mb-4"><ListChecks className="h-4 w-4 text-primary" />Commitments & follow-ups</div>
          {canEdit && <TaskForm ents={state.data?.ents || []} onSaved={state.reload} />}
          {state.data?.tasks.length ? <TaskList tasks={state.data.tasks} reload={state.reload} canEdit={canEdit} />
            : <div className="mt-6"><Empty icon={ListChecks} title="Nothing tracked yet" text="Add commitments and follow-ups so your briefing can keep you on top of them." testId="tasks-empty" /></div>}
        </div>
      </PageState>
    </div>
  );
}
